import React, { useMemo, useState, useEffect } from "react";
import {
  Container,
  Flex,
  Text,
  Box,
  Link,
  Divider,
  useColorModeValue,
} from "@chakra-ui/react";

import SearchPager from "../SearchPager";
import { grants } from "../DATABASE/researchGrants";

function matchesQuery(item, query, keys) {
  if (!query.trim()) return true;
  const q = query.toLowerCase();
  return keys.some((k) => String(item?.[k] ?? "").toLowerCase().includes(q));
}

function paginate(list, page, pageSize) {
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    totalPages,
    safePage,
    pageItems: list.slice(start, start + pageSize),
  };
}

export default function ResearchGrants() {
  const uoRed = "#8F001A";
  const uoRedHover = "#6E0014";

  const textColor = useColorModeValue("gray.700", "gray.300");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const panelBg = useColorModeValue("white", "gray.800");
  const cardBg = useColorModeValue("white", "gray.900");

  // ---- Search + Pagination
  const PAGE_SIZE = 8;
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => setPage(1), [query]);

  const linkStyle = {
    color: uoRed,
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    fontWeight: 600,
    _hover: { color: uoRedHover, textDecorationThickness: "2px" },
    cursor: "pointer",
  };

  const filtered = useMemo(() => {
    const keys = ["title", "date", "text"];
    return grants.filter((g) => matchesQuery(g, query, keys));
  }, [query]);

  const pg = useMemo(() => paginate(filtered, page, PAGE_SIZE), [filtered, page]);

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(pg.totalPages, p + 1));

  return (
    <>
      {/* Page Title */}
      <Container maxW="1100px" py={6}>
        <Text fontSize={{ base: "26px", md: "32px" }} fontWeight="900" textAlign="center">
          Research Grants
        </Text>

        <Text textAlign="center" mt={2} fontSize="lg" color={textColor}>
          <Link href="/" {...linkStyle}>
            Dr. Burak Kantarci
          </Link>
        </Text>

        <Divider mt={6} />
      </Container>

      <Container maxW="1200px" pb={10}>
        {/* Global Search */}
        <Box mt={5}>
          <SearchPager
            mode="search"
            title="Search Grants & Projects"
            query={query}
            onQueryChange={setQuery}
            page={1}
            pageSize={PAGE_SIZE}
            totalItems={grants.length}
            filteredItems={filtered.length}
            onPrev={() => {}}
            onNext={() => {}}
          />
        </Box>

        {/* Panel wrapper (same as Awards) */}
        <Box
          mt={6}
          bg={panelBg}
          border={`1px solid ${borderColor}`}
          borderRadius="18px"
          px={{ base: 3, md: 4 }}
          pt={{ base: 3, md: 4 }}
          pb={{ base: 2, md: 3 }}
        >
          {/* ===== UPDATED HEADER: title first row, pager second row under title ===== */}
          <Box mb={2}>
            {/* Row 1: Title */}
            <Flex align="center" justify="space-between" wrap="wrap" gap={2}>
              <Text
                fontSize="lg"
                fontWeight="800"
                textDecoration="underline"
                textUnderlineOffset="4px"
                textDecorationThickness="2px"
                lineHeight="1.2"
              >
                Research Grants and Funded Projects
              </Text>
            </Flex>

            {/* Row 2: Pager UNDER title (left aligned) */}
            {pg.totalPages > 1 && (
              <Flex mt={2} align="center" justify="flex-start" wrap="wrap" gap={2}>
                <Flex align="center" gap={2}>
                  <Box
                    as="button"
                    onClick={goPrev}
                    disabled={pg.safePage <= 1}
                    style={{
                      border: "1px solid",
                      borderColor: "inherit",
                      borderRadius: "999px",
                      padding: "4px 10px",
                      opacity: pg.safePage <= 1 ? 0.45 : 1,
                      cursor: pg.safePage <= 1 ? "not-allowed" : "pointer",
                    }}
                  >
                    ‹
                  </Box>

                  <Text fontSize="sm" minW="88px" textAlign="center">
                    Page{" "}
                    <Text as="span" fontWeight="700">
                      {pg.safePage}
                    </Text>{" "}
                    / {pg.totalPages}
                  </Text>

                  <Box
                    as="button"
                    onClick={goNext}
                    disabled={pg.safePage >= pg.totalPages}
                    style={{
                      border: "1px solid",
                      borderColor: "inherit",
                      borderRadius: "999px",
                      padding: "4px 10px",
                      opacity: pg.safePage >= pg.totalPages ? 0.45 : 1,
                      cursor: pg.safePage >= pg.totalPages ? "not-allowed" : "pointer",
                    }}
                  >
                    ›
                  </Box>
                </Flex>
              </Flex>
            )}
          </Box>
          {/* ===== END UPDATED HEADER ===== */}

          {/* Empty state */}
          {filtered.length === 0 ? (
            <Box mt={3}>
              <Text color="gray.500">No grants match your search.</Text>
            </Box>
          ) : (
            <>
              {pg.pageItems.map((grant, index) => (
                <Box
                  key={`${grant.title}-${index}`}
                  mt={3}
                  position="relative"
                  bg={cardBg}
                  border={`1px solid ${borderColor}`}
                  borderRadius="16px"
                  p={{ base: 3, md: 4 }}
                  pl={{ base: 4, md: 5 }} // space for accent strip
                  _hover={{
                    borderColor: useColorModeValue("rgba(143,0,26,0.35)", "rgba(143,0,26,0.55)"),
                    boxShadow: useColorModeValue(
                      "0 10px 24px rgba(0,0,0,0.08)",
                      "0 10px 24px rgba(0,0,0,0.35)"
                    ),
                  }}
                  transition="all 160ms ease"
                >
                  {/* Accent strip */}
                  <Box
                    position="absolute"
                    left="0"
                    top="10px"
                    bottom="10px"
                    w="4px"
                    borderRadius="999px"
                    bg={uoRed}
                    opacity={0.95}
                  />

                  {/* Title + Date */}
                  <Flex justify="space-between" align="baseline" wrap="wrap" gap={2}>
                    <Text fontWeight="900" color={textColor} flex="1" minW={{ base: "100%", md: "65%" }}>
                      {grant.title}
                    </Text>

                    <Text
                      fontWeight="800"
                      fontStyle="italic"
                      color={useColorModeValue("gray.600", "gray.400")}
                      minW={{ base: "100%", md: "20%" }}
                      textAlign={{ base: "left", md: "right" }}
                    >
                      {grant.date}
                    </Text>
                  </Flex>

                  {/* Description */}
                  <Text mt={2} color={textColor} lineHeight="1.75">
                    {grant.text}
                  </Text>
                </Box>
              ))}
            </>
          )}
        </Box>
      </Container>
    </>
  );
}